
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { convert, conversionTypes, type ConversionCategory } from '@/utils/conversions';

const categories: ConversionCategory[] = Array.from(new Set(conversionTypes.map(type => type.category))).sort();

const Converter = () => {
  const [category, setCategory] = useState<ConversionCategory>('length');
  const [fromUnit, setFromUnit] = useState('');
  const [toUnit, setToUnit] = useState('');
  const [value, setValue] = useState<string>('');

  // Get unique "from" units for the selected category
  const uniqueFromUnits = Array.from(
    new Set(
      conversionTypes
        .filter(type => type.category === category)
        .map(type => type.from)
    )
  ).sort();

  // Get available "to" units based on selected "from" unit
  const availableToUnits = conversionTypes
    .filter(type => type.category === category && type.from === fromUnit)
    .map(type => type.to)
    .sort();

  const result = value && fromUnit && toUnit ? convert(parseFloat(value), fromUnit, toUnit) : null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FDE1D3]">
      <Card className="w-full max-w-md mx-4 shadow-lg border-none">
        <CardHeader className="bg-[#1A1F2C] text-white rounded-t-lg">
          <CardTitle className="text-2xl font-bold text-center">Unit Converter</CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          <div className="space-y-2">
            <Label htmlFor="category">Category</Label>
            <Select
              value={category}
              onValueChange={(value: ConversionCategory) => {
                setCategory(value);
                setFromUnit('');
                setToUnit('');
                setValue('');
              }}
            >
              <SelectTrigger id="category" className="w-full">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat} className="capitalize">
                    {cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="from">From</Label>
            <Select
              value={fromUnit}
              onValueChange={(value) => {
                setFromUnit(value);
                setToUnit('');
                setValue('');
              }}
            >
              <SelectTrigger id="from" className="w-full">
                <SelectValue placeholder="Select unit" />
              </SelectTrigger>
              <SelectContent>
                {uniqueFromUnits.map((unit) => (
                  <SelectItem key={unit} value={unit} className="capitalize">
                    {unit}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {fromUnit && (
            <div className="space-y-2">
              <Label htmlFor="to">To</Label>
              <Select
                value={toUnit}
                onValueChange={setToUnit}
              >
                <SelectTrigger id="to" className="w-full">
                  <SelectValue placeholder="Select unit" />
                </SelectTrigger>
                <SelectContent>
                  {availableToUnits.map((unit) => (
                    <SelectItem key={unit} value={unit} className="capitalize">
                      {unit}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {fromUnit && toUnit && (
            <div className="space-y-2">
              <Label htmlFor="value">Value</Label>
              <Input
                id="value"
                type="number"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Enter value"
                className="w-full"
              />
            </div>
          )}

          {result !== null && (
            <div className="mt-6 p-4 bg-[#FFA500]/10 rounded-lg">
              <p className="text-lg font-medium text-center">
                {value} {fromUnit} = {result.toFixed(2)} {toUnit}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Converter;

